import { useCallback } from "react";
import { FeatureDc } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { useCreateFeature } from "../../../../hooks/feature/useCreateFeature";
import { usePostgresLayer } from "../../../../hooks/layer/usePostgresLayer";
import { usePostgresLayerTableName } from "../../../../hooks/layer/usePostgresLayerWithTable";
import { useNotification } from "../../../../hooks/notifications/useNotification";
import { useDialogReset } from "../../../../hooks/useDialogs";
import { useFeatureCard } from "../../../../modules/FeatureCard/hooks/useFeatureCard";

import { FeatureDcExtended } from "../../../../ducks/selectFeatures/types";

interface CopyFeature {
  loading: boolean;
  onCopy: VoidFunction;
}

export const useCopyFeatureJob = (layerName: string, feature: FeatureDcExtended): CopyFeature => {
  const { t } = useTranslation("dialogs");
  const tableName = usePostgresLayerTableName(layerName);
  const { postgresLayer: targetLayerInfo } = usePostgresLayer(layerName, { waitInput: true });
  const [copyFeature, { data: jobResult, loading: jobLoading, error: jobError }] = useCreateFeature(
    layerName,
    tableName,
  );
  const closeDialog = useDialogReset();
  const [, { closeCard }] = useFeatureCard();

  useNotification(jobResult && !jobLoading, {
    success: true,
    title: t("copyFeature.featureCopySuccess"),
  });

  useNotification(jobError, {
    error: true,
    title: t("copyFeature.featureCopyError"),
  });

  const onCopy = useCallback(() => {
    if (
      !layerName ||
      !feature ||
      !targetLayerInfo ||
      (targetLayerInfo?.type === "PostgresLayerService" && !tableName)
    ) {
      return;
    }

    const { layerAttributes } = targetLayerInfo;
    const { attributes } = feature;

    const commonAttributes =
      Array.isArray(layerAttributes) && attributes
        ? Object.keys(attributes).reduce((acc, key) => {
            if (layerAttributes.findIndex(item => item.attributeName === key) > -1) {
              return {
                ...acc,
                [key]: attributes[key],
              };
            }

            return acc;
          }, {})
        : {};

    copyFeature({
      ...feature,
      attributes: commonAttributes,
    } as FeatureDc).then(() => {
      closeDialog();
      closeCard();
    });
  }, [layerName, tableName, feature, targetLayerInfo, copyFeature, closeDialog, closeCard]);

  return { loading: jobLoading, onCopy };
};
